import { z } from 'zod';
import { OffsetPaginationParamsSchema } from '../offset-pagination.js';
// ---------------------------------------------------------------------------
// Kit shipments and site inventory (domain 5, REQ-KIT-007, REQ-KIT-008,
// REQ-KIT-019, REQ-KIT-026, REQ-KIT-027, REQ-KIT-034, REQ-KIT-035).
// ---------------------------------------------------------------------------
export const KitShipmentItemDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    shipmentId: z.number().int().positive(),
    labKitId: z.number().int().positive(),
    labKitExpirationId: z.number().int().positive().nullable(),
    batchNumber: z.string().nullable(),
    expirationDate: z.iso.date().nullable(),
    quantity: z.number().int().positive(),
    comments: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** Enriched field for UI presentation */
    kitName: z.string().optional(),
});
export const CreateKitShipmentItemRequestSchema = z.object({
    labKitId: z.number().int().positive({ message: 'Wybór typu zestawu jest wymagany' }),
    batchNumber: z.string().max(100).optional(),
    /**
     * Expiration date: ISO date YYYY-MM-DD or month-only YYYY-MM (normalised by service),
     * or null/omitted for open-ended kits (expiration = false).
     */
    expirationDate: z
        .union([
        z.iso.date(),
        z
            .string()
            .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Podaj datę w formacie RRRR-MM-DD albo sam miesiąc jako RRRR-MM'),
    ])
        .nullable()
        .optional(),
    quantity: z
        .number({ message: 'Ilość musi być liczbą całkowitą' })
        .int()
        .positive({ message: 'Ilość przyjętych zestawów musi być większa od zera' }),
    comments: z.string().max(10_000).optional(),
});
export const KitShipmentDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    shipmentNumber: z.string().min(1).max(100),
    courierName: z.string().nullable(),
    receptionDate: z.iso.date(),
    temperatureCondition: z.string().nullable(),
    temperatureOk: z.boolean(),
    comments: z.string().nullable(),
    createdByUserId: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
    items: z.array(KitShipmentItemDtoSchema).optional(),
});
export const CreateKitShipmentRequestSchema = z.object({
    trialId: z.number().int().positive({ message: 'Identyfikator badania jest wymagany' }),
    shipmentNumber: z.string().min(1, 'Numer przesyłki/listu jest wymagany').max(100),
    courierName: z.string().max(100).optional(),
    receptionDate: z.iso.date({ message: 'Data przyjęcia przesyłki jest wymagana' }),
    temperatureCondition: z.string().max(50).optional(),
    temperatureOk: z.boolean().default(true),
    comments: z.string().max(10_000).optional(),
    items: z
        .array(CreateKitShipmentItemRequestSchema)
        .min(1, 'Przesyłka musi zawierać co najmniej jedną pozycję materiałową'),
});
export const KitShipmentListQuerySchema = OffsetPaginationParamsSchema.extend({
    trialId: z.coerce.number().int().positive(),
    search: z.string().max(100).optional(),
});
/**
 * Single batch/expiration stock representation for FEFO ordering and expiry alerts.
 * REQ-KIT-019, REQ-KIT-026.
 */
export const KitBatchStockDtoSchema = z.object({
    expirationId: z.number().int().positive().nullable(),
    batchNumber: z.string().nullable(),
    expirationDate: z.iso.date().nullable(),
    quantity: z.number().int(),
    isExpired: z.boolean(),
    daysUntilExpiration: z.number().int().nullable(),
});
/**
 * Inventory stock state for a specific kit type in a trial.
 * REQ-KIT-027.
 */
export const KitStockItemDtoSchema = z.object({
    labKitId: z.number().int().positive(),
    kitName: z.string(),
    trialLaboratoryId: z.number().int().positive(),
    laboratoryName: z.string(),
    expiration: z.boolean(),
    localisation: z.string().nullable(),
    totalStock: z.number().int(),
    batches: z.array(KitBatchStockDtoSchema),
});
export const KitStockQuerySchema = z.object({
    trialId: z.coerce.number().int().positive(),
    trialLaboratoryId: z.coerce.number().int().positive().optional(),
});
//# sourceMappingURL=shipments.schema.js.map